import { Button, Checkbox, CircularProgress, Stack } from "@mui/material";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import { useEffect, useMemo, useRef, useState } from "react";
import { setupService } from "../../../../Core/Api/services";
import { DbStatusTransitionInfos } from "../../../../Core/Api/Types/db-status-transition-infos";
import { ObjectStatus } from "../../../../Core/Api/Types/object-status";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
import { SaveOutlined } from "@mui/icons-material";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../Components/Table/table-uuids";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";

function AdminSettingsStatusTransitions() {
    const { t } = useTranslation();
    const [statusTransitions, setStatusTransitions] = useState<DbStatusTransitionInfos[] | null>(null);
    const updatedStatusTransitions = useRef<DbStatusTransitionInfos[] | null>(null);
    const [assetStatusEnum, setAssetStatusEnum] = useState<ObjectStatus[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const enqueueCustomSnackbar = useCustomSnackbar();
    const apiRef = useGridApiRef();

    useEffect(() => {
        Promise.all([setupService.GetStatusTransitions(), setupService.GetMetadataFieldEnums()]).then(
            ([statusTransitionsResponse, metadataFieldResponse]) => {
                setStatusTransitions(statusTransitionsResponse.transitions);
                updatedStatusTransitions.current = statusTransitionsResponse.transitions;
                setAssetStatusEnum(metadataFieldResponse.assetStatusEnums ?? []);
                setIsLoading(false);
            }
        );
    }, []);

    const handleChange = (e: any, rowId: number, status: string, field: string) => {
        if (!statusTransitions || !assetStatusEnum) return;
        if (apiRef.current) {
            let updatedRow = { id: rowId, status: status };
            if (updatedStatusTransitions.current) {
                const updatedStrIndex = updatedStatusTransitions.current.findIndex((x) => x.inputStatus === status);
                for (var n = 0; n < assetStatusEnum.length; n++) {
                    updatedRow[`${assetStatusEnum[n]}`] =
                        updatedStrIndex === -1 || updatedStatusTransitions.current[updatedStrIndex].outputStatuses === null
                            ? false
                            : updatedStatusTransitions.current[updatedStrIndex].outputStatuses!.indexOf(assetStatusEnum[n]) >= 0;
                }
            }
            apiRef.current.updateRows([{ ...updatedRow, [field]: e.target.checked }]);
        }
        if (!updatedStatusTransitions.current) return;
        const newUpdatedStatusTransitions = [
            ...updatedStatusTransitions.current.map((x) =>
                x.inputStatus === status
                    ? ({
                        ...x,
                        outputStatuses: e.target.checked
                            ? x.outputStatuses
                                ? [...x.outputStatuses, field]
                                : [field]
                            : x.outputStatuses
                                ? [...x.outputStatuses.filter((s) => s !== field)]
                                : [],
                    } as DbStatusTransitionInfos)
                    : x
            ),
        ];
        updatedStatusTransitions.current = newUpdatedStatusTransitions;
    };

    const columns: GridColDef[] = useMemo(() => {
        if (!assetStatusEnum) return [];

        return [
            {
                headerName: `${t("common:input")} / ${t("common:output")}`,
                field: "status",
                flex: CellSize.xlarge,
                minWidth: CellSize.xlarge,
                sortable: false,
            },
            ...assetStatusEnum.map((x, i) => ({
                headerName: x,
                field: x,
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                sortable: false,
                renderCell: (data: any) => (
                    <Checkbox checked={data.value} onChange={(e) => handleChange(e, data.id, data?.row?.status, data.field)} />
                ),
            })),
        ];
    }, [assetStatusEnum]);

    const rows: any[] = useMemo(() => {
        if (!assetStatusEnum || !statusTransitions) return [];
        const result: any[] = [];
        for (var i = 0; i < assetStatusEnum.length; i++) {
            const row: any = { id: i + 1, status: assetStatusEnum[i] };
            const inputIndex = statusTransitions.findIndex((x) => x.inputStatus === assetStatusEnum[i]);

            for (var n = 0; n < assetStatusEnum.length; n++) {
                row[`${assetStatusEnum[n]}`] =
                    inputIndex === -1 || statusTransitions[inputIndex].outputStatuses === null
                        ? false
                        : statusTransitions[inputIndex].outputStatuses!.indexOf(assetStatusEnum[n]) >= 0;
            }
            result.push(row);
        }
        return result;
    }, [statusTransitions, assetStatusEnum]);

    const updateStatusTransitions = () => {
        setIsSaving(true);
        if (!updatedStatusTransitions.current) return;
        setupService.UpdateStatusTransitions(updatedStatusTransitions.current).then((res) => {
            setIsSaving(false);
            if (res.success) {
                enqueueCustomSnackbar(t("alert:success.statusTransitionsUpdated"), "success");
                setStatusTransitions(updatedStatusTransitions.current);
            } else {
                enqueueCustomSnackbar(res.message ?? t("alert:error.updatingStatusTransitions"), "error");
            }
        });
    };

    const additionalActions = (
        <Button
            disabled={isLoading || isSaving || updatedStatusTransitions === null}
            onClick={updateStatusTransitions}
            color="primary"
            variant="contained"
            fullWidth
            endIcon={isSaving ? <CircularProgress size={16} color="success" /> : <SaveOutlined />}
        >
            {t("button:updateTransitions")}
        </Button>
    );

    return (
        <>
            {isLoading ? (<TableSkeleton />) : (
                <DataTable
                    tableKey={TableUuids.SETTINGS_STATUS_TRANSITIONS}
                    apiRef={apiRef}
                    rows={rows}
                    columns={columns}
                    additionalActions={additionalActions}
                />
            )}
        </>
    );
}

export default AdminSettingsStatusTransitions;
